import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  useDeleteAgentsById,
  getGetAgentsQueryKey,
} from '@/api/control/control-plane/control-plane'
import type { AgentDto } from '@/api/control/missionImpactAPI.schemas'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface DeleteAgentDialogProps {
  orgId: string
  agent: AgentDto
}

/**
 * Dialog that confirms deletion of a self-hosted agent.
 */
export function DeleteAgentDialog({ orgId, agent }: DeleteAgentDialogProps) {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const { handleApiError } = useError()
  const [isOpen, setIsOpen] = useState(false)

  const { mutate: deleteAgent, isPending } = useDeleteAgentsById({
    mutation: {
      onSuccess: () => {
        toast.success(t.agents.deleteDialog.success)
        void queryClient.invalidateQueries({
          queryKey: getGetAgentsQueryKey(orgId),
        })
        setIsOpen(false)
      },
      onError: (error) => {
        handleApiError(error, { context: 'DeleteAgentDialog.deleteAgent' })
      },
    },
  })

  const handleDelete = () => {
    deleteAgent({ orgId, agentId: agent.id })
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t.agents.deleteDialog.triggerLabel}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>{t.agents.deleteDialog.title}</DialogTitle>
          </div>
          <DialogDescription>
            {t.agents.deleteDialog.description.replace('{name}', agent.name)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { setIsOpen(false); }}
            disabled={isPending}
          >
            {t.agents.deleteDialog.cancel}
          </Button>
          <LoadingButton
            type="button"
            variant="destructive"
            onClick={handleDelete}
            isLoading={isPending}
          >
            {t.agents.deleteDialog.delete}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
